import { createSelector } from "reselect";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  BasicBotData,
  BotData,
  GetBotGraphDataRequest,
  GetBotsDataRequest,
  GetBotsGraphsDataRequest,
  GetPublicBotGraphDataRequest,
} from "@shared/types";
import {
  filterBots,
  getFormattedBotGraphData,
  getFormattedBotsGraphsData,
} from "@shared/features/bots/utils";
import { sharedLib } from "@shared/services/shared.service";
import { BOT_TYPE_TO_FILTERS_SLICE } from "@shared/features/bots/constants";

export const selectFormattedBotGraphData = (
  args: GetBotGraphDataRequest,
  botData?: BotData
) =>
  createSelector(
    sharedLib.sharedApi.bots.endpoints.getBotGraphData.select(args),
    selectDisplayCurrency,
    (botGraphData, displayCurrency) => {
      return getFormattedBotGraphData(
        botGraphData?.data,
        args,
        displayCurrency,
        botData
      );
    }
  );

export const selectFormattedPublicBotGraphData = (
  args: GetPublicBotGraphDataRequest,
  botData?: BotData,
  totalChangePercent?: number
) =>
  createSelector(
    sharedLib.sharedApi.bots.endpoints.getPublicBotGraphData.select(args),
    selectDisplayCurrency,
    (botGraphData, displayCurrency) => {
      return getFormattedBotGraphData(
        botGraphData?.data,
        args,
        displayCurrency,
        botData,
        totalChangePercent
      );
    }
  );

export const selectBotsData = (args: GetBotsDataRequest) =>
  createSelector(
    sharedLib.sharedApi.bots.endpoints.getBotsData.select(args),
    (botsDataQuery) => {
      return botsDataQuery?.data?.data as
        | BotData[]
        | BasicBotData[]
        | undefined;
    }
  );

export const selectFilteredBots = (args: GetBotsDataRequest) =>
  createSelector(
    selectBotsData(args),
    BOT_TYPE_TO_FILTERS_SLICE[args.bot_type].selectors.selectFiltersData,
    (botsData, filtersData) => {
      return filterBots(botsData, filtersData);
    }
  );

export const selectFormattedBotsGraphsData = (
  args: GetBotsGraphsDataRequest
) => {
  const getBotsDataArgs: GetBotsDataRequest = {
    bot_type: args.bot_type,
    include_archived: args.include_archived,
  };

  return createSelector(
    selectFilteredBots(getBotsDataArgs),
    sharedLib.sharedApi.bots.endpoints.getBotsGraphsData.select(args),
    selectDisplayCurrency,
    (filteredBotsData, botsGraphsData, displayCurrency) => {
      const { data: graphData, precision: precisionConfig } =
        botsGraphsData?.data || {};
      return getFormattedBotsGraphsData(
        filteredBotsData,
        displayCurrency,
        precisionConfig,
        graphData
      );
    }
  );
};
